/**
 * Real-time Event System
 * Provides pub/sub pattern for instant communication between chatbot and admin
 * Simulates WebSocket-like real-time behavior without requiring a backend socket server
 */

class EventEmitter {
  constructor() {
    this.subscribers = {};
    this.eventQueue = [];
    this.maxQueueSize = 100;
  }

  /**
   * Subscribe to events
   * @param {string} eventType - Type of event to listen for
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(eventType, callback) {
    if (!this.subscribers[eventType]) {
      this.subscribers[eventType] = [];
    }

    const subscriber = {
      id: `${eventType}-${Date.now()}-${Math.random()}`,
      callback,
    };

    this.subscribers[eventType].push(subscriber);

    // Return unsubscribe function
    return () => {
      this.subscribers[eventType] = this.subscribers[eventType].filter(
        (sub) => sub.id !== subscriber.id
      );
    };
  }

  /**
   * Subscribe to event once
   * @param {string} eventType - Type of event
   * @param {Function} callback - Callback function
   */
  once(eventType, callback) {
    const unsubscribe = this.on(eventType, (...args) => {
      callback(...args);
      unsubscribe();
    });
  }

  /**
   * Emit event to all subscribers
   * @param {string} eventType - Type of event
   * @param {Object} data - Event data
   */
  emit(eventType, data) {
    const event = {
      type: eventType,
      data,
      timestamp: new Date().toISOString(),
      id: `event-${Date.now()}-${Math.random()}`,
    };

    // Add to queue
    this.eventQueue.push(event);
    if (this.eventQueue.length > this.maxQueueSize) {
      this.eventQueue.shift();
    }

    // Call all subscribers
    if (this.subscribers[eventType]) {
      this.subscribers[eventType].forEach((subscriber) => {
        try {
          subscriber.callback(data, event.id);
        } catch (error) {
          console.error(
            `[EventEmitter] Error in ${eventType} subscriber:`,
            error
          );
        }
      });
    }

    // Also broadcast via localStorage for cross-tab support
    try {
      localStorage.setItem(
        `realtime-event-${Date.now()}`,
        JSON.stringify(event)
      );
      // Clean up old entries
      this.cleanupLocalStorageEvents();
    } catch (error) {
      console.error('[EventEmitter] Error broadcasting to localStorage:', error);
    }
  }

  /**
   * Clear old localStorage events
   * @private
   */
  cleanupLocalStorageEvents() {
    try {
      const keys = Object.keys(localStorage);
      const eventKeys = keys.filter((k) => k.startsWith('realtime-event-'));
      if (eventKeys.length > 50) {
        // Remove oldest 10
        eventKeys.slice(0, 10).forEach((k) => localStorage.removeItem(k));
      }
    } catch (error) {
      console.error('[EventEmitter] Error cleaning localStorage:', error);
    }
  }

  /**
   * Get all events of a specific type
   * @param {string} eventType - Type of event to filter
   * @returns {Array} Array of events
   */
  getEventsByType(eventType) {
    return this.eventQueue.filter((e) => e.type === eventType);
  }

  /**
   * Get last event of a specific type
   * @param {string} eventType - Type of event
   * @returns {Object|null} Last event or null
   */
  getLastEvent(eventType) {
    const events = this.getEventsByType(eventType);
    return events.length > 0 ? events[events.length - 1] : null;
  }

  /**
   * Clear event queue
   */
  clearQueue() {
    this.eventQueue = [];
  }

  /**
   * Get subscriber count for event type
   * @param {string} eventType - Type of event
   * @returns {number} Number of subscribers
   */
  getSubscriberCount(eventType) {
    return (this.subscribers[eventType] || []).length;
  }

  /**
   * Get all event types with active subscribers
   * @returns {Array} Array of event types
   */
  getActiveEventTypes() {
    return Object.keys(this.subscribers).filter(
      (key) => this.subscribers[key].length > 0
    );
  }
}

// Create singleton instance
export const realtimeEvents = new EventEmitter();

/**
 * Event types used throughout the system
 */
export const EVENT_TYPES = {
  // Escalation events
  ESCALATION_INITIATED: 'ESCALATION_INITIATED',
  ESCALATION_CONFIRMED: 'ESCALATION_CONFIRMED',
  ESCALATION_TIMEOUT: 'ESCALATION_TIMEOUT',

  // Message events
  MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
  MESSAGE_SENT: 'MESSAGE_SENT',
  MESSAGE_DELIVERED: 'MESSAGE_DELIVERED',
  MESSAGES_SYNCED: 'MESSAGES_SYNCED',

  // Ticket events
  TICKET_CREATED: 'TICKET_CREATED',
  TICKET_UPDATED: 'TICKET_UPDATED',
  TICKET_CLOSED: 'TICKET_CLOSED',
  TICKET_ASSIGNED: 'TICKET_ASSIGNED',

  // Admin events
  ADMIN_JOINED: 'ADMIN_JOINED',
  ADMIN_TYPING: 'ADMIN_TYPING',
  ADMIN_RESPONSE_RECEIVED: 'ADMIN_RESPONSE_RECEIVED',

  // User events
  USER_CONNECTED: 'USER_CONNECTED',
  USER_DISCONNECTED: 'USER_DISCONNECTED',

  // System events
  SYNC_REQUEST: 'SYNC_REQUEST',
  CONNECTION_STATUS: 'CONNECTION_STATUS',
};

/**
 * Helper function to emit escalation event
 */
export const emitEscalation = (escalationData) => {
  realtimeEvents.emit(EVENT_TYPES.ESCALATION_INITIATED, escalationData);
};

/**
 * Helper function to emit message event
 */
export const emitMessage = (messageData) => {
  realtimeEvents.emit(EVENT_TYPES.MESSAGE_RECEIVED, messageData);
};

/**
 * Helper function to emit ticket update
 */
export const emitTicketUpdate = (ticketData) => {
  realtimeEvents.emit(EVENT_TYPES.TICKET_UPDATED, ticketData);
};

/**
 * Helper function to emit connection status
 */
export const emitConnectionStatus = (status) => {
  realtimeEvents.emit(EVENT_TYPES.CONNECTION_STATUS, {
    status, // 'connected', 'disconnected', 'reconnecting'
    timestamp: new Date().toISOString(),
  });
};

export default realtimeEvents;
